import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Trophy,
  BarChart3,
  Radio,
  Calendar,
  Globe,
  ArrowRight,
  Check,
  Zap
} from 'lucide-react';

const LandingPage = () => {
  const features = [
    {
      icon: Trophy,
      title: 'Tournament Management',
      description: 'Support for T20, ODI, Test, and T10 formats with flexible scheduling.',
    },
    {
      icon: Users,
      title: 'Team & Player Management',
      description: 'Complete roster management with detailed player profiles and statistics.',
    },
    {
      icon: Radio,
      title: 'Live Scoring',
      description: 'Ball-by-ball scoring with real-time updates and automatic calculations.',
    },
    {
      icon: BarChart3,
      title: 'Advanced Analytics',
      description: 'Comprehensive scorecards, partnerships, and performance insights.',
    },
    {
      icon: Calendar,
      title: 'Match Scheduling',
      description: 'Venue assignments, umpire management, and complete match lifecycle.',
    },
    {
      icon: Globe,
      title: 'Public Scoreboards',
      description: 'Share live scores with fans via public URLs. No login required.',
    }
  ];

  const benefits = [
    'Multi-format support (T20, ODI, Test, T10)',
    'Real-time ball-by-ball updates',
    'Automatic statistics calculation',
    'Team and player management',
    'Public shareable scorecards',
    'Mobile-friendly interface'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="/logo.png" alt="Innings Pro" className="h-9 w-auto" />
              <span className="text-xl font-semibold text-gray-900">Innings Pro</span>
            </div>
            <div className="flex items-center space-x-6">
              <Link
                to="/register"
                className="hidden sm:inline-flex text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                Register
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 text-green-700 text-sm font-medium mb-6">
              <Zap className="h-4 w-4 mr-2" />
              Professional Cricket Management Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-tight">
              Run your cricket tournaments like a pro
            </h1>
            <p className="mt-6 text-xl text-gray-600 leading-relaxed">
              The all-in-one platform for organizing tournaments, managing teams, and delivering live scores to your fans.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-sm"
              >
                Start Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl font-bold text-gray-900">
              Everything you need to manage cricket tournaments
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              From scheduling matches to live scoring, we've got you covered.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mb-5">
                  <feature.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Built for cricket organizers
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're running a local club tournament or a league, Innings Pro gives you the tools to manage everything in one place.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-3xl p-8 lg:p-12 text-white">
              <div className="text-green-200 text-sm font-medium mb-4">GET STARTED</div>
              <h3 className="text-2xl lg:text-3xl font-bold mb-4">
                Create your tournament in minutes
              </h3>
              <p className="text-green-100 mb-8">
                Sign up, create your organization, and start managing your cricket tournaments today.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center px-6 py-3 text-base font-medium text-green-600 bg-white rounded-lg hover:bg-gray-50 transition-colors"
              >
                Create Free Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to streamline your cricket management?
          </h2>
          <p className="text-xl text-gray-400 mb-10">
            Join teams already using Innings Pro for their tournaments.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-colors"
            >
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-6 md:mb-0">
              <img src="/logo.png" alt="Innings Pro" className="h-8 w-auto" />
              <span className="text-lg font-semibold text-white">Innings Pro</span>
            </div>
            <div className="flex items-center space-x-8 text-sm">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                Sign In
              </Link>
              <Link to="/register" className="text-gray-400 hover:text-white transition-colors">
                Register
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm text-gray-500">
            © {new Date().getFullYear()} Innings Pro. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
